import React, { useEffect, useMemo, useState } from "react";
import { Truck, Download, CheckCircle2, AlertTriangle, Copy, Mail, MapPin, Package } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

const fmtMoney = (n) => `£${Number(n || 0).toFixed(2)}`;

const orderNotes = (o) => {
  const cust = (o.customer_notes || "").trim();
  const pack = (o.notes || "").trim();
  return { cust, pack: pack && pack !== cust ? pack : "" };
};

const NotesBanner = ({ orders }) => {
  const noted = orders.filter((o) => orderNotes(o).cust || orderNotes(o).pack);
  if (noted.length === 0) return null;
  return (
    <div className="pg-card p-4 mb-4 border-[#FF8A5B]/60 bg-[#FF8A5B]/[0.06]" data-testid="dispatch-notes-banner">
      <div className="pg-micro text-[#FF8A5B] mb-2 flex items-center gap-2">
        <AlertTriangle size={11} /> {noted.length} order{noted.length === 1 ? " carries" : "s carry"} a note — read before you print labels
      </div>
      <ul className="space-y-1.5">
        {noted.map((o) => {
          const n = orderNotes(o);
          return (
            <li key={o.id} className="text-xs text-[#F7FAFC] flex flex-wrap gap-x-2" data-testid={`notes-banner-${o.order_number}`}>
              <span className="pg-mono text-[#9CA8B8]">{o.order_number}</span>
              <span className="font-semibold">{o.shipping?.full_name}</span>
              {n.cust && <span className="text-[#FFB38A]">“{n.cust}”</span>}
              {n.pack && <span className="text-[#FF6B6B] font-semibold">PACK: {n.pack}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const RowNotes = ({ o }) => {
  const n = orderNotes(o);
  if (!n.cust && !n.pack) return null;
  return (
    <div className="md:col-span-11 md:col-start-2 flex flex-col gap-1.5 -mt-1" data-testid={`row-notes-${o.order_number}`}>
      {n.cust && (
        <div className="text-xs text-[#0B1220] bg-[#FFB38A] rounded-sm px-3 py-2 leading-snug">
          <span className="pg-mono text-[10px] uppercase tracking-[0.18em] font-bold mr-2">Customer note</span>{n.cust}
        </div>
      )}
      {n.pack && (
        <div className="text-xs text-white bg-[#C0392B] rounded-sm px-3 py-2 leading-snug">
          <span className="pg-mono text-[10px] uppercase tracking-[0.18em] font-bold mr-2">Packing instruction</span>{n.pack}
        </div>
      )}
    </div>
  );
};

const PickingList = ({ orders, selected }) => {
  const scope = selected.size > 0 ? orders.filter((o) => selected.has(o.id)) : orders;
  const rows = useMemo(() => {
    const bySku = {};
    for (const o of scope) {
      for (const i of o.items || []) {
        const key = i.sku || i.name;
        if (!bySku[key]) bySku[key] = { sku: i.sku, name: i.name, strength: i.strength, qty: 0, gift: 0 };
        bySku[key].qty += i.quantity || 0;
        if (i.is_gift) bySku[key].gift += i.quantity || 0;
      }
    }
    return Object.values(bySku).sort((a, b) => b.qty - a.qty);
  }, [scope]);

  if (orders.length === 0) return null;
  const totalVials = rows.reduce((s, r) => s + r.qty, 0);

  const copyList = () => {
    const text = rows.map((r) => `${r.qty}× ${r.name} ${r.strength || ""}`.trim()).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Picking list copied");
  };

  return (
    <div className="pg-card p-5 mb-4" data-testid="picking-list">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="pg-micro flex items-center gap-2 text-[#5EEAD4]">
          <Package size={11} /> Picking list
          <span className="text-[#9CA8B8] normal-case tracking-normal">
            — {totalVials} item{totalVials === 1 ? "" : "s"} across {scope.length} order{scope.length === 1 ? "" : "s"}
            {selected.size > 0 ? " (selected only)" : ""}
          </span>
        </div>
        <button onClick={copyList} className="pg-btn-ghost text-[10px] flex items-center gap-1" data-testid="copy-picking-list">
          <Copy size={10} /> Copy list
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {rows.map((r) => (
          <div
            key={r.sku || r.name}
            className="flex items-center gap-3 bg-[#0B1220] border border-[#1E2E44] rounded-sm px-3 py-2"
            data-testid={`picking-row-${r.sku}`}
          >
            <div className="pg-mono text-[#A6FF00] text-lg font-bold w-10 text-right shrink-0">{r.qty}×</div>
            <div className="min-w-0">
              <div className="text-[#F7FAFC] text-sm truncate">
                {r.name} <span className="text-[#5EEAD4] text-xs">{r.strength}</span>
              </div>
              <div className="text-[#9CA8B8] text-[10px] pg-mono truncate">
                {r.sku}{r.gift > 0 ? ` · incl. ${r.gift} free gift` : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminDispatch() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [trackingMap, setTrackingMap] = useState({}); // order_id -> tracking_number
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/dispatch/queue");
      setOrders(r.data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Your access has been removed — please speak to David");
        localStorage.removeItem("pg_admin_token");
        window.location.assign("/staff-portal-72f4");
        return;
      }
      toast.error("Failed to load dispatch queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const allSelectedIds = useMemo(() => orders.map((o) => o.id), [orders]);
  const allSelected = selected.size > 0 && selected.size === orders.length;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allSelectedIds));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadCsv = async () => {
    try {
      const ids = selected.size > 0 ? Array.from(selected).join(",") : "";
      const token = localStorage.getItem("pg_admin_token") || "";
      const url = `${REACT_APP_BACKEND_URL}/api/admin/dispatch/royal-mail-csv${ids ? `?order_ids=${encodeURIComponent(ids)}` : ""}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      link.download = `peptigen-dispatch-${today}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      const count = selected.size || orders.length;
      toast.success(`Downloaded CSV for ${count} order${count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("CSV download failed");
    }
  };

  const submitBulkDispatch = async () => {
    const entries = Array.from(selected)
      .map((id) => ({
        order_id: id,
        tracking_number: (trackingMap[id] || "").trim(),
      }))
      .filter((e) => e.tracking_number);

    if (entries.length === 0) {
      toast.error("Paste at least one tracking number before marking dispatched");
      return;
    }
    if (entries.length !== selected.size) {
      toast.error(`Missing tracking numbers on ${selected.size - entries.length} selected order(s)`);
      return;
    }

    setSubmitting(true);
    try {
      const r = await api.post("/admin/dispatch/bulk-mark-shipped", { entries });
      const { successful, processed, results } = r.data;
      const failed = results.filter((x) => !x.ok);
      if (failed.length === 0) {
        toast.success(`${successful} order${successful === 1 ? "" : "s"} marked dispatched · emails sent`);
      } else {
        toast.error(`${successful}/${processed} dispatched · ${failed.length} failed (${failed.map((f) => f.error).join(", ")})`);
      }
      setSelected(new Set());
      setTrackingMap({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk dispatch failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-dispatch-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="pg-micro mb-2 flex items-center gap-2"><Truck size={11} /> Dispatch Queue</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Today's post run
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-1 max-w-2xl">
            Every paid order not yet shipped. Tick the rows you're sending out, download the
            Click &amp; Drop CSV, then paste tracking numbers below to mark them dispatched in bulk.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="pg-btn-ghost text-xs" data-testid="reload-dispatch">
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            disabled={orders.length === 0}
            className="pg-btn-primary text-xs flex items-center gap-2 disabled:opacity-50"
            data-testid="download-rm-csv"
          >
            <Download size={13} /> Royal Mail CSV
            {selected.size > 0 && (
              <span className="text-[10px] bg-black/30 rounded-sm px-1.5 py-0.5">{selected.size} selected</span>
            )}
          </button>
        </div>
      </div>

      {/* How-to */}
      <details className="pg-card p-4 mb-4 text-sm" data-testid="dispatch-howto">
        <summary className="cursor-pointer text-[#5EEAD4] pg-micro hover:text-[#A6FF00] transition-colors">
          // How to use this page
        </summary>
        <ol className="text-[#9CA8B8] text-xs leading-relaxed mt-3 space-y-1.5 list-decimal pl-5">
          <li>Tick the orders you're posting today (or leave all unticked to export everything).</li>
          <li>Click <strong className="text-[#A6FF00]">Royal Mail CSV</strong> to download today's dispatch file.</li>
          <li>
            Go to
            {" "}
            <a href="https://business.parcel.royalmail.com/" target="_blank" rel="noopener noreferrer" className="text-[#5EEAD4] underline">
              Royal Mail Click &amp; Drop
            </a>
            {" "}
            → <em>Import Orders</em> → drag the CSV in → <em>Buy &amp; Print</em>.
          </li>
          <li>Stick the labels on the parcels, drop at the post office.</li>
          <li>
            Back here: paste each tracking number into the box next to its order, then click
            {" "}
            <strong className="text-[#A6FF00]">Mark all dispatched</strong>. The customer dispatch
            email fires automatically for each one.
          </li>
        </ol>
      </details>

      {/* Notes first — the thing that gets missed when you go straight to the CSV */}
      <NotesBanner orders={orders} />

      {/* Picking list — aggregated vial counts across the queue */}
      <PickingList orders={orders} selected={selected} />

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="pg-card p-3 mb-4 flex items-center justify-between gap-3 border-[#A6FF00]/40 sticky top-2 z-30"
          data-testid="dispatch-bulk-bar"
        >
          <div className="text-sm text-[#F7FAFC]">
            <strong className="text-[#A6FF00]">{selected.size}</strong> order{selected.size === 1 ? "" : "s"} selected
            <span className="text-[#9CA8B8] ml-2">— paste tracking numbers below, then:</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="pg-btn-ghost text-xs"
              data-testid="clear-selection"
            >
              Clear
            </button>
            <button
              onClick={submitBulkDispatch}
              disabled={submitting}
              className="pg-btn-primary text-xs flex items-center gap-2 disabled:opacity-50"
              data-testid="mark-all-dispatched"
            >
              <CheckCircle2 size={13} />
              {submitting ? "Sending…" : `Mark ${selected.size} dispatched`}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="pg-card divide-y divide-[#00C2D6]/10">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] pg-mono uppercase tracking-[0.18em] text-[#5EEAD4] items-center">
          <div className="col-span-1">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="accent-[#A6FF00]"
              data-testid="select-all"
            />
          </div>
          <div className="col-span-3">Order</div>
          <div className="col-span-3">Recipient + address</div>
          <div className="col-span-2">Items</div>
          <div className="col-span-3">Tracking number</div>
        </div>

        {loading ? (
          <div className="text-[#9CA8B8] text-center py-12">Loading dispatch queue…</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle2 size={32} className="text-[#A6FF00] mx-auto mb-3" />
            <div className="text-[#F7FAFC] text-base">All caught up — no orders awaiting dispatch.</div>
            <div className="text-[#9CA8B8] text-xs mt-1">New paid orders will appear here automatically.</div>
          </div>
        ) : (
          orders.map((o) => {
            const checked = selected.has(o.id);
            const s = o.shipping || {};
            return (
              <div
                key={o.id}
                className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-5 py-4 items-start ${checked ? "bg-[#A6FF00]/5" : ""}`}
                data-testid={`dispatch-row-${o.order_number}`}
              >
                <div className="md:col-span-1 flex items-start pt-1">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOne(o.id)}
                    className="accent-[#A6FF00]"
                    data-testid={`select-${o.order_number}`}
                  />
                </div>
                <div className="md:col-span-3 min-w-0">
                  <div className="pg-mono text-[#F7FAFC] text-xs truncate">{o.order_number}</div>
                  <div className="text-[#9CA8B8] text-[11px] mt-0.5">{fmtDate(o.created_at)}</div>
                  {o.total !== undefined && <div className="text-[#A6FF00] pg-mono text-sm mt-1">{fmtMoney(o.total)}</div>}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(o.order_number);
                      toast.success("Order ref copied");
                    }}
                    className="text-[10px] pg-mono text-[#5EEAD4] hover:text-[#A6FF00] mt-1 flex items-center gap-1"
                    data-testid={`copy-ref-${o.order_number}`}
                  >
                    <Copy size={10} /> copy ref
                  </button>
                </div>
                <div className="md:col-span-3 text-sm text-[#F7FAFC] min-w-0">
                  <div className="truncate">{s.full_name || "(no name)"}</div>
                  <div className="text-[#9CA8B8] text-[12px] flex items-start gap-1 mt-1">
                    <MapPin size={11} className="mt-0.5 shrink-0" />
                    <span className="leading-tight">
                      {s.address_line1}
                      {s.address_line2 ? `, ${s.address_line2}` : ""}
                      <br />
                      {s.city} {(s.postcode || "").toUpperCase()}
                    </span>
                  </div>
                  {s.email && (
                    <div className="text-[#9CA8B8] text-[11px] mt-1 flex items-center gap-1 truncate">
                      <Mail size={10} /> {s.email}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 text-[12px] text-[#9CA8B8]">
                  {(o.items || []).map((i, idx) => (
                    <div key={idx} className="leading-tight">
                      <span className="text-[#F7FAFC]">{i.quantity}×</span> {i.name} {i.strength}
                    </div>
                  ))}
                </div>
                <div className="md:col-span-3">
                  <input
                    type="text"
                    placeholder="Paste tracking #"
                    value={trackingMap[o.id] || ""}
                    onChange={(e) => setTrackingMap({ ...trackingMap, [o.id]: e.target.value })}
                    onFocus={() => {
                      // Auto-select the order so the user doesn't have to do
                      // tick+paste — one less click per dispatch.
                      if (!checked) toggleOne(o.id);
                    }}
                    className="pg-input text-xs w-full pg-mono"
                    data-testid={`tracking-input-${o.order_number}`}
                  />
                  {(o.address_line1 || "").length > 0 && !/\d/.test(o.address_line1 || "") && (
                    <div className="text-[10px] text-[#FF8A5B] mt-1 flex items-center gap-1">
                      <AlertTriangle size={10} /> Address may be missing a house number
                    </div>
                  )}
                </div>
                <RowNotes o={o} />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
